import { useEffect, useState } from 'preact/hooks';
import { apiBaseLabel } from '../api-base.js';
import { type ApiKeySummary, DashboardApi, type MemberSummary, type UsageSummary } from '../api.js';
import type { AuthOrg, AuthUser } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

interface OnboardingStep {
  id: 'key' | 'invite' | 'call';
  title: string;
  done: boolean;
  body: preact.ComponentChildren;
  cta?: { label: string; page: string };
}

const navTo = (page: string) => {
  window.dispatchEvent(new CustomEvent('whs-nav', { detail: page }));
};

export function Overview({
  user,
  org,
}: {
  user: AuthUser;
  org: AuthOrg;
}): preact.ComponentChildren {
  const [health, setHealth] = useState<{ status: string; provider: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [keys, setKeys] = useState<ApiKeySummary[] | null>(null);
  const [members, setMembers] = useState<MemberSummary[] | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);

  useEffect(() => {
    api
      .health()
      .then((h) =>
        setHealth({
          status: h.status,
          provider: h.llm.providerConfigured ? h.llm.model : 'mock',
        }),
      )
      .catch((e) => setError(String(e.message ?? e)));
  }, []);

  useEffect(() => {
    void (async () => {
      const [k, m, u] = await Promise.all([
        api.listApiKeys(org.id).catch(() => ({ keys: [] })),
        api.listMembers(org.id).catch(() => ({ members: [] })),
        api.getUsage(org.id).catch(() => null),
      ]);
      setKeys(k.keys);
      setMembers(m.members);
      setUsage(u);
    })();
  }, [org.id]);

  const hasActiveKey = (keys ?? []).some((k) => !k.revokedAt);
  const hasTeammate = (members ?? []).filter((m) => !m.self).length > 0;
  const hasFirstCall = (usage?.used ?? 0) > 0;
  const onboardingComplete = hasActiveKey && hasTeammate && hasFirstCall;

  const steps: OnboardingStep[] = [
    {
      id: 'key',
      title: 'Create your first API key',
      done: hasActiveKey,
      body: (
        <p class="small muted">
          Keys authenticate the embed script, the GitHub Action, and any custom integration. Treat
          them like passwords.
        </p>
      ),
      cta: { label: 'Open Keys', page: 'keys' },
    },
    {
      id: 'invite',
      title: 'Invite a teammate',
      done: hasTeammate,
      body: (
        <p class="small muted">
          Add an editor or viewer so you're not the only person who can manage{' '}
          <strong>{org.name}</strong>.
        </p>
      ),
      cta: { label: 'Open Members', page: 'members' },
    },
    {
      id: 'call',
      title: 'Make your first API call',
      done: hasFirstCall,
      body: (
        <>
          <p class="small muted">
            Once you have a key, try the <code>describe</code> route:
          </p>
          <pre
            style={{
              background: 'var(--bg-subtle, #1a1f29)',
              padding: 12,
              borderRadius: 6,
              overflowX: 'auto',
              fontSize: 12,
            }}
          >
            {`curl https://${apiBaseLabel()}/v1/describe \\
  -H "x-api-key: $WHOLISPHERE_API_KEY" \\
  -H "content-type: application/json" \\
  -d '{"image":{"mimeType":"image/png","data":"<base64>"}}'`}
          </pre>
        </>
      ),
    },
  ];

  return (
    <>
      <div class="page-header">
        <h1>Overview</h1>
        <p>
          Welcome, {user.displayName ?? user.email}. Working in <strong>{org.name}</strong> (
          {org.plan}).
        </p>
      </div>

      {error && <Banner variant="warn">Backend unreachable: {error}</Banner>}

      <div class="kpi-grid">
        <Kpi label="Backend status" value={health?.status ?? '…'} delta={apiBaseLabel()} />
        <Kpi label="Active LLM" value={health?.provider ?? '…'} delta="vision route" />
        <Kpi
          label="Plan"
          value={org.plan}
          delta={org.plan === 'free' ? 'upgrade in Billing' : 'manage in Billing'}
        />
        <Kpi label="Region" value={org.region.toUpperCase()} delta="data residency" />
      </div>

      {!onboardingComplete && (
        <div class="card">
          <h2>Get started</h2>
          <p class="desc">
            {steps.filter((s) => s.done).length} of {steps.length} done.
          </p>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {steps.map((s) => (
              <li
                key={s.id}
                style={{
                  padding: '12px 0',
                  borderBottom: '1px solid var(--border, #1f2733)',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  aria-hidden="true"
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: s.done ? 'var(--success)' : 'var(--bg-subtle, #1a1f29)',
                    color: s.done ? '#fff' : 'var(--fg-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 24px',
                    fontWeight: 600,
                  }}
                >
                  {s.done ? '✓' : ''}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      textDecoration: s.done ? 'line-through' : 'none',
                      color: s.done ? 'var(--fg-muted)' : 'var(--fg)',
                    }}
                  >
                    {s.title}
                  </div>
                  {!s.done && <div style={{ marginTop: 6 }}>{s.body}</div>}
                  {!s.done && s.cta && (
                    <button
                      type="button"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        if (s.cta) navTo(s.cta.page);
                      }}
                    >
                      {s.cta.label}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {onboardingComplete && (
        <div class="card">
          <h2>You're set up</h2>
          <p class="desc">
            All three onboarding steps complete. Watch real visitor activity stream into
            <strong> Audit log</strong>, generate a VPAT under <strong>Compliance</strong>, and wire
            the GitHub Action for cache invalidation on every deploy.
          </p>
        </div>
      )}
    </>
  );
}

function Kpi({ label, value, delta }: { label: string; value: string; delta?: string }) {
  return (
    <div class="kpi">
      <div class="label">{label}</div>
      <div class="value">{value}</div>
      {delta && <div class="delta">{delta}</div>}
    </div>
  );
}
